<div class="companies-page">
  <div class="companies-header">
    <div class="header-left">
      <h1>Companies</h1>
      <p>
        Showing page {{ pageNumber }} of {{ totalPages || 1 }} 
        (Total {{ totalCount }} companies)
      </p>
    </div>

    <div class="header-right">
      <span class="helper-text" title="Use +/- to adjust available credit">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="info-icon">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clip-rule="evenodd" />
        </svg>
        Use +/- to adjust available credit
      </span>
      <div class="search-box">
        <span class="search-icon">⌕</span>
        <input 
          type="text" 
          [value]="searchTerm" 
          (input)="onSearchChange($event)" 
          placeholder="Search..." 
        />
      </div>

      <button type="button" class="icon-filter-btn" title="Filters">⌄</button>

      <button type="button" class="create-btn" (click)="openCreateModal()">
        Create
      </button>
    </div>
  </div>

  @if (isLoading) {
    <div class="empty-state">Loading companies...</div>
  } @else {
    <div class="companies-table-card">
      <div class="table-scroll">
        <table class="companies-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>PHONE</th>
              <th>ADDRESS 1</th>
              <th>ADDRESS 2</th>
              <th>CITY</th>
              <th>STATE</th>
              <th>ZIP</th>
              <th>ACTIVE</th>
              <th>AVAILABLE CREDIT</th>
              <th>CREDIT USED</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            @for (company of companies; track company.companyId) {
              <tr>
                <td>{{ company.companyId }}</td>
                <td class="truncate-cell company-name">{{ company.name }}</td>
                <td class="truncate-cell">{{ company.email }}</td>
                <td>{{ company.phoneNumber || '' }}</td>
                <td class="truncate-cell">{{ company.addressLine1 }}</td>
                <td class="truncate-cell">{{ company.addressLine2 || '' }}</td>
                <td>{{ company.city }}</td>
                <td>{{ company.state }}</td>
                <td>{{ company.zipCode }}</td>
                <td>
                  <span class="status-badge" [class.active]="company.isActive">
                    {{ company.isActive ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <div class="wallet-cell">
                    <button type="button" class="btn-wallet" (click)="openWalletModal(company, 'Debit')">-</button>
                    <span class="wallet-amount">{{ getSafeWalletBalance(company.availableCredits) | currency:'USD' }}</span>
                    <button type="button" class="btn-wallet" (click)="openWalletModal(company, 'Credit')">+</button>
                  </div>
                </td>
                <td>
                  {{ getSafeWalletBalance(company.usedCredits) | currency:'USD' }}
                </td>
                <td>
                  <div class="action-buttons">
                    <button type="button" class="btn-edit" (click)="openEditModal(company)">
                      ✎ <span>Edit</span>
                    </button>
                    <button type="button" class="btn-config" (click)="onConfig(company)">
                      ⊹ <span>Config</span>
                    </button>
                    <button
                      type="button"
                      class="icon-action delete"
                      (click)="onDeleteCompany(company)"
                      [disabled]="deletingCompanyId === company.companyId"
                      title="Delete"
                    >
                      <img src="/icons/delete.svg" alt="Delete" class="action-icon" />
                    </button>
                  </div>
                </td>
              </tr>
            }
            @if (companies.length === 0) {
              <tr>
                <td colspan="12" class="text-center py-4">No companies found.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div class="pagination-controls">
        <button 
          type="button" 
          class="btn-pagination" 
          (click)="previousPage()" 
          [disabled]="pageNumber === 1 || isLoading">
          Previous
        </button>
        <span class="page-info">Page {{ pageNumber }} of {{ totalPages || 1 }}</span>
        <button 
          type="button" 
          class="btn-pagination" 
          (click)="nextPage()" 
          [disabled]="pageNumber >= totalPages || isLoading">
          Next
        </button>
      </div>
    </div>
  }

  @if (isModalOpen) {
    <div class="modal-overlay" (click)="closeModal()">
      <div class="company-modal" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <div>
            <h2>{{ isEditMode ? 'Edit Company' : 'Create New Company' }}</h2>
            <p>
              {{
                isEditMode
                  ? 'Update company information.'
                  : 'Add a new company to the system. Fill in all required fields.'
              }}
            </p>
          </div>
          <button type="button" class="close-btn" (click)="closeModal()">×</button>
        </div>

        <form [formGroup]="companyForm" (ngSubmit)="saveCompany()" class="modal-form">
          <div class="form-grid two-col">
            <div class="form-field">
              <label>Name *</label>
              <input type="text" formControlName="name" />
              @if (nameControl?.touched && nameControl?.errors?.['required']) {
                <div class="field-error">Name is required.</div>
              }
            </div>

            <div class="form-field">
              <label>Email *</label>
              <input type="email" formControlName="email" />
              @if (emailControl?.touched && emailControl?.errors?.['required']) {
                <div class="field-error">Email is required.</div>
              }
              @if (emailControl?.touched && emailControl?.errors?.['email']) {
                <div class="field-error">Invalid email format.</div>
              }
            </div>
          </div>

          <div class="form-grid two-col">
            <div class="form-field">
              <label>Address 1</label>
              <input type="text" formControlName="addressLine1" />
            </div>

            <div class="form-field">
              <label>Address 2</label>
              <input type="text" formControlName="addressLine2" />
            </div>
          </div>

          <div class="form-grid two-col">
            <div class="form-field">
              <label>Phone Number</label>
              <input type="text" formControlName="phoneNumber" />
            </div>

            <div class="form-field">
              <label>State</label>
              <select formControlName="state">
                <option value="">Select State</option>
                @for (st of usStates; track st.isoCode) {
                  <option [value]="st.isoCode">{{ st.name }} ({{ st.isoCode }})</option>
                }
              </select>
            </div>
          </div>

          <div class="form-grid three-col">
            <div class="form-field">
              <label>City</label>
              <select formControlName="city">
                <option value="">Select City</option>
                @for (city of usCities; track city.name) {
                  <option [value]="city.name">{{ city.name }}</option>
                }
              </select>
            </div>

            <div class="form-field">
              <label>Zip Code</label>
              <input type="text" formControlName="zipCode" />
            </div>

            <div class="form-field">
              <label>Country</label>
              <input type="text" formControlName="country" readonly />
            </div>
          </div>

          <div class="form-grid one-col">
            <div class="form-field">
              <label>Status</label>
              <select formControlName="status">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-cancel" (click)="closeModal()">Cancel</button>
            <button type="submit" class="btn-save" [disabled]="isSaving">
              {{
                isSaving
                  ? isEditMode
                    ? 'Updating...'
                    : 'Creating...'
                  : isEditMode
                    ? 'Update Company'
                    : 'Create Company'
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  }

  <!-- Pricing Modal Maintained -->
  @if (isPricingModalOpen) {
    <div class="modal-overlay" (click)="closePricingModal()">
      <div class="company-modal pricing-modal" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <div>
            <h2>Company Pricing</h2>
            <p>
              Pricing configuration for
              <strong>{{ selectedPricingCompany?.name }}</strong>
            </p>
          </div>
          <button type="button" class="close-btn" (click)="closePricingModal()">×</button>
        </div>

        @if (isPricingLoading) {
          <div class="empty-state">Loading pricing...</div>
        } @else if (pricingErrorMessage && !isPricingSaving && pricingItemsArray.length === 0) {
          <div class="empty-state error">{{ pricingErrorMessage }}</div>
        } @else {
          <form [formGroup]="pricingForm">
            <div class="pricing-table-wrap">
              <table class="pricing-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Description</th>
                    <th>Cost</th>
                    <th>Sale Price</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody formArrayName="items">
                  @for (row of pricingItemsArray.controls; track $index) {
                    <tr [formGroupName]="$index">
                      <td class="pricing-service">{{ row.get('service')?.value }}</td>
                      <td>{{ row.get('description')?.value }}</td>

                      <td>
                        @if (row.get('serviceCode')?.value === 'ESTIMATED_COST_CHARGE') {
                          <input
                            type="number"
                            class="pricing-input"
                            formControlName="cost"
                            step="0.01"
                            placeholder="Enter percentage"
                          />
                        } @else {
                          <input
                            type="number"
                            class="pricing-input"
                            formControlName="cost"
                            step="0.01"
                            placeholder="Enter cost"
                          />
                        }
                      </td>

                      <td>
                        @if (row.get('serviceCode')?.value === 'ESTIMATED_COST_CHARGE') {
                          <span class="muted-dash">—</span>
                        } @else {
                          <input
                            type="number"
                            class="pricing-input"
                            formControlName="salePrice"
                            step="0.01"
                            placeholder="Enter sale price"
                          />
                        }
                      </td>

                      <td>{{ row.get('unit')?.value }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </form>
        }

        @if (pricingErrorMessage && pricingItemsArray.length > 0) {
          <div class="pricing-error-text">{{ pricingErrorMessage }}</div>
        }

        <div class="modal-actions">
          <button type="button" class="btn-cancel" (click)="closePricingModal()">Cancel</button>
          <button
            type="button"
            class="btn-save"
            (click)="savePricing()"
            [disabled]="isPricingLoading || isPricingSaving"
          >
            {{ isPricingSaving ? 'Saving...' : 'Save Pricing' }}
          </button>
        </div>
      </div>
    </div>
  }

  <div class="companies-footer">
    <a href="javascript:void(0)">Privacy Policy</a>
    <a href="javascript:void(0)">Terms of Service</a>
    <a href="javascript:void(0)">Contact</a>
  </div>
</div>

@if (isWalletModalOpen) {
  <div class="modal-overlay" (click)="closeWalletModal()">
    <div class="company-modal delete-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div>
          <h2>{{ walletAdjustmentType === 'Credit' ? 'Add Credit' : 'Debit Credit' }}</h2>
          <p>
            {{ walletAdjustmentType === 'Credit' ? 'Add funds to' : 'Remove funds from' }} <strong>{{ walletAdjustingCompany?.name }}</strong>.
          </p>
        </div>
        <button type="button" class="close-btn" (click)="closeWalletModal()">×</button>
      </div>

      <form [formGroup]="walletForm" (ngSubmit)="submitWalletAdjustment()" class="modal-form">
        <div class="form-grid one-col">
          <div class="form-field">
            <label>Amount (USD) *</label>
            <input type="number" formControlName="amount" step="0.01" min="0.01" placeholder="Enter amount" />
            @if (walletForm.get('amount')?.touched && walletForm.get('amount')?.errors?.['required']) {
              <div class="field-error">Amount is required.</div>
            }
            @if (walletForm.get('amount')?.touched && walletForm.get('amount')?.errors?.['min']) {
              <div class="field-error">Amount must be greater than 0.</div>
            }
          </div>

          <div class="form-field">
            <label>Reason *</label>
            <input type="text" formControlName="reason" placeholder="Enter reason" />
            @if (walletForm.get('reason')?.touched && walletForm.get('reason')?.errors?.['required']) {
              <div class="field-error">Reason is required.</div>
            }
          </div>
        </div>

        @if (walletErrorMessage) {
          <div class="field-error" style="margin-top: 10px;">{{ walletErrorMessage }}</div>
        }

        <div class="modal-actions">
          <button type="button" class="btn-cancel" (click)="closeWalletModal()">Cancel</button>
          <button type="submit" class="btn-save" [disabled]="isWalletSaving">
            {{ isWalletSaving ? 'Processing...' : (walletAdjustmentType === 'Credit' ? 'Add Credit' : 'Debit Credit') }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

@if (isDeleteModalOpen()) {
  <div class="modal-overlay" (click)="closeDeleteModal()">
    <div class="delete-modal" (click)="$event.stopPropagation()">
      <div class="delete-modal-header">
        <h3>Delete Company</h3>
        <button type="button" class="modal-close-btn" (click)="closeDeleteModal()">×</button>
      </div>

      <div class="delete-modal-body">
        <p>
          Are you sure you want to delete
          <strong>{{ deletingCompany?.name }}</strong>?
        </p>
      </div>

      <div class="delete-modal-actions">
        <button type="button" class="delete-cancel-btn" (click)="closeDeleteModal()">Cancel</button>
        <button
          type="button"
          class="delete-confirm-btn"
          (click)="confirmDeleteCompany()"
          [disabled]="deletingCompanyId !== null"
        >
          {{ deletingCompanyId !== null ? 'Deleting...' : 'Delete' }}
        </button>
      </div>
    </div>
  </div>
}

@if (errorMessage) {
  <div class="global-error-overlay" (click)="clearError()">
    <div class="error-toast" (click)="$event.stopPropagation()">
      <div class="error-icon">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <div class="error-content">
        <h3>Something went wrong</h3>
        <p>{{ errorMessage }}</p>
        <div class="error-actions">
          <button type="button" class="btn-error-ok" (click)="clearError()">OK</button>
        </div>
      </div>
      <button type="button" class="close-toast-btn" (click)="clearError()">×</button>
    </div>
  </div>
}
